import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  HomeIcon, 
  BriefcaseIcon,
  DocumentTextIcon,
  UserGroupIcon,
  MapIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

// Define navigation groups
const navigationGroups = [
  {
    name: 'Main',
    items: [
      { name: 'Dashboard', icon: HomeIcon, to: '/dashboard' },
    ]
  },
  {
    name: 'Business',
    items: [
      { name: 'Jobs & Invoicing', icon: BriefcaseIcon, to: '/jobs', submenu: [
        { name: 'Create Job', to: '/jobs?tab=create' },
        { name: 'Manage Jobs', to: '/jobs?tab=manage' },
        { name: 'Invoices', to: '/jobs?tab=invoices' },
        { name: 'Services', to: '/jobs?tab=services' },
      ]},
      { name: 'Quotes', icon: DocumentTextIcon, to: '/quotes', submenu: [
        { name: 'Create Quote', to: '/quotes?tab=create' },
        { name: 'Manage Quotes', to: '/quotes?tab=manage' },
        { name: 'Templates', to: '/quotes?tab=templates' },
      ]},
      { name: 'Customers', icon: UserGroupIcon, to: '/customers', submenu: [
        { name: 'Create Customer', to: '/customers?tab=create' },
        { name: 'Manage Customers', to: '/customers?tab=manage' },
        { name: 'Customer Map', to: '/customers?tab=map' },
      ]},
    ]
  },
  {
    name: 'Operations',
    items: [
      { name: 'Routes', icon: MapIcon, to: '/route-map', submenu: [
        { name: 'Route Map', to: '/route-map' },
        { name: 'Calendar View', to: '/calendar' },
      ]},
      { name: 'Employees', icon: UsersIcon, to: '/employees', submenu: [
        { name: 'Manage Employees', to: '/employees?tab=manage' },
        { name: 'Create Employee', to: '/employees?tab=create' },
        { name: 'Manage Crews', to: '/employees?tab=crews' },
      ]},
    ]
  },
  {
    name: 'Analytics & Tools',
    items: [
      { name: 'Analytics', icon: ChartBarIcon, to: '/analytics' },
      { name: 'Tools', icon: WrenchScrewdriverIcon, to: '#', submenu: [
        { name: 'Material Calculator', to: '/material-calculator' },
        { name: 'Generate Report', to: '/generate-report' },
        { name: 'Send Notifications', to: '/send-notifications' },
        { name: 'Usage Statistics', to: '/profile?tab=usage' },
      ]},
    ]
  },
  {
    name: 'Account',
    items: [
      { name: 'Profile', icon: Cog6ToothIcon, to: '/profile' },
    ]
  }
];

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedItem, setExpandedItem] = useState(null);
  
  // On location change, close mobile sidebar
  useEffect(() => {
    setMobileOpen(false);
  }, [location, setMobileOpen]);

  // Find the currently active group and item based on location
  const findActiveItem = () => {
    const path = location.pathname;
    
    for (const group of navigationGroups) {
      for (const item of group.items) {
        if (path === item.to || path.startsWith(item.to.split('?')[0])) {
          return item.name;
        }
        
        if (item.submenu) {
          for (const subItem of item.submenu) {
            if (path === subItem.to || path.startsWith(subItem.to.split('?')[0])) {
              return item.name;
            }
          }
        }
      }
    }
    
    return null;
  };

  useEffect(() => {
    setExpandedItem(findActiveItem());
  }, [location]);

  const toggleExpand = (itemName) => {
    if (expandedItem === itemName) {
      setExpandedItem(null);
    } else {
      setExpandedItem(itemName);
    }
  };

  const isActive = (to) => {
    return location.pathname === to || location.pathname.startsWith(to.split('?')[0]);
  };
  
  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <img className="h-8 w-auto" src="/logo.png" alt="AutoLawn" />
          <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            AutoLawn
          </span>
        </Link>
        <button 
          className="md:hidden text-gray-400 hover:text-white"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {navigationGroups.map((group) => (
          <div key={group.name} className="space-y-2">
            <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {group.name}
            </p>
            <div className="space-y-1">
              {group.items.map((item) => (
                <div key={item.name} className="space-y-1">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleExpand(item.name)}
                        className={`flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive(item.to) 
                            ? 'bg-gradient-to-r from-blue-500/60 to-purple-600/60 text-white' 
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </div>
                        {expandedItem === item.name ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                      
                      {expandedItem === item.name && (
                        <div className="mt-1 ml-4 pl-3 border-l border-gray-700 space-y-1">
                          {item.submenu.map((subItem) => (
                            <NavLink
                              key={subItem.name}
                              to={subItem.to}
                              className={({ isActive }) =>
                                `block px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                  isActive
                                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-600/30 text-white'
                                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                }`
                              }
                            >
                              {subItem.name}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-gradient-to-r from-blue-500/60 to-purple-600/60 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="p-4 border-t border-gray-700">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0) || "U"}
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-white">{user?.name || "User"}</p>
            <p className="text-xs text-gray-400 mt-0.5">{user?.email || "user@example.com"}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition duration-200"
        >
          <ArrowLeftOnRectangleIcon className="h-4 w-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="md:hidden fixed top-4 left-4 z-50 inline-flex items-center justify-center p-3 rounded-lg text-gray-300 bg-gray-800/90 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white shadow-lg border border-gray-700"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-expanded={mobileOpen}
      >
        <span className="sr-only">Open main menu</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>
      
      {/* Mobile sidebar backdrop */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out"
          onClick={() => setMobileOpen(false)}
        ></div>
      )}
      
      {/* Sidebar for desktop */}
      <div className="hidden md:flex md:flex-col md:w-64 md:flex-shrink-0 bg-gray-800 border-r border-gray-700">
        {sidebarContent}
      </div>
      
      {/* Sidebar for mobile - slide in from left */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 w-full sm:w-64 transform ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out z-50 bg-gray-800 shadow-lg overflow-hidden`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-shrink-0 p-4 flex items-center justify-between border-b border-gray-700">
            <Link to="/" className="flex items-center space-x-2">
              <img className="h-8 w-auto" src="/logo.png" alt="AutoLawn" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                AutoLawn
              </span>
            </Link>
            <button 
              className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700"
              onClick={() => setMobileOpen(false)}
            >
              <XMarkIcon className="h-7 w-7" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-4">
            {navigationGroups.map((group) => (
              <div key={group.name} className="space-y-1">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.name}
                </p>
                
                {group.items.map((item) => (
                  <div key={item.name} className="space-y-1">
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => toggleExpand(item.name)}
                          className={`flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                            isActive(item.to) 
                              ? 'bg-gradient-to-r from-blue-500/60 to-purple-600/60 text-white' 
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <item.icon className="h-5 w-5" />
                            <span>{item.name}</span>
                          </div>
                          {expandedItem === item.name ? (
                            <ChevronUpIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>
                        
                        {expandedItem === item.name && (
                          <div className="mt-1 ml-3 pl-3 border-l border-gray-700 space-y-1">
                            {item.submenu.map((subItem) => (
                              <NavLink
                                key={subItem.name}
                                to={subItem.to}
                                className={({ isActive }) =>
                                  `block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                                    isActive
                                      ? 'bg-gradient-to-r from-blue-500/30 to-purple-600/30 text-white'
                                      : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                  }`
                                }
                                onClick={() => setMobileOpen(false)}
                              >
                                {subItem.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center space-x-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                            isActive
                              ? 'bg-gradient-to-r from-blue-500/60 to-purple-600/60 text-white'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`
                        }
                        onClick={() => setMobileOpen(false)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </NavLink>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t border-gray-700 bg-gray-800/70">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0) || "U"}
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user?.name || "User"}</p>
                <p className="text-xs text-gray-400 mt-0.5">{user?.email || "user@example.com"}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition duration-200"
            >
              <ArrowLeftOnRectangleIcon className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 
